/**
 * Homepage "Follow the Artist — Explore the Music" section.
 * Reads the centralized /api/social-profiles feed via useSocialProfiles.
 * Hides entirely when both categories are empty (initial load or API down).
 */
import { useTranslation } from "react-i18next";
import { useSocialProfiles } from "@/lib/useSocialProfiles";
import { PlatformIcon } from "@/lib/platformIcons";

function ProfileGrid({ label, items, testidPrefix }) {
  if (!items.length) return null;
  return (
    <div className="mt-10 first:mt-0" data-testid={`${testidPrefix}-group`}>
      <div className="font-mono text-[10px] tracking-[0.3em] text-white/50 mb-4" data-testid={`${testidPrefix}-label`}>
        {label}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
        {items.map((p) => (
          <a
            key={p.platform_slug}
            href={p.url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`DJ SPY on ${p.display_name}`}
            data-testid={`social-platform-${p.platform_slug}`}
            className="group flex items-center gap-3 rounded-lg border border-white/10 bg-white/[0.02] hover:bg-white/[0.06] hover:border-red-brand/40 px-4 py-3 transition-colors"
          >
            <PlatformIcon slug={p.icon_hint || p.platform_slug} size={22} className="opacity-90 group-hover:opacity-100 transition-opacity flex-shrink-0" />
            <span className="text-sm md:text-base text-white/85 group-hover:text-white truncate" dir="ltr">
              {p.display_name}
            </span>
          </a>
        ))}
      </div>
    </div>
  );
}

export default function SocialMusicPlatforms() {
  const { i18n } = useTranslation();
  const { music_platforms, social_media, loading } = useSocialProfiles();
  const isAr = (i18n.language || "").startsWith("ar");

  // Hide until data present (per user directive — no hardcoded fallback).
  if (loading) return null;
  if (!music_platforms.length && !social_media.length) return null;

  const eyebrow = isAr ? "// DJ SPY الرسمي" : "// DJ SPY OFFICIAL";
  const heading = isAr
    ? "تابع DJ SPY واكتشف موسيقاه"
    : "FOLLOW THE ARTIST.\nEXPLORE THE MUSIC.";
  const musicLabel = isAr ? "منصات الموسيقى" : "MUSIC PLATFORMS";
  const socialLabel = isAr ? "وسائل التواصل الاجتماعي" : "SOCIAL MEDIA";

  return (
    <section
      id="social-platforms"
      data-testid="section-social-platforms"
      className="relative py-20 md:py-24 border-t border-white/10"
    >
      <div className="max-w-[1400px] mx-auto px-6">
        <div className="mb-8 md:mb-10">
          <p
            className={`font-mono text-[10px] tracking-[0.3em] text-red-brand mb-3 ${isAr ? "text-right" : ""}`}
            dir={isAr ? undefined : "ltr"}
            data-testid="social-eyebrow"
          >
            {eyebrow}
          </p>
          <h2
            className="font-anton text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white whitespace-pre-line"
            data-testid="social-heading"
          >
            <bdi>{heading}</bdi>
          </h2>
        </div>

        <ProfileGrid label={musicLabel}  items={music_platforms} testidPrefix="social-music" />
        <ProfileGrid label={socialLabel} items={social_media}    testidPrefix="social-social" />
      </div>
    </section>
  );
}
